import { CLASS_RELATIVE_TIMING_VERSION } from '@/lib/session-result-timing'

export const CAR_CLASS_SCORING_VERSION = 1

export const CAR_CLASS_SCORING_VERSION_KEY = 'carClassScoringVersion'
export const CAR_CLASS_SNAPSHOT_KEY = 'championshipCarClassSnapshot'
export const RESULT_CAR_CLASS_KEY = 'championshipCarClassId'

function asMetadata(value) {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : {}
}

export function getChampionshipCarClassSnapshot(result) {
  const snapshot = asMetadata(result?.metadata)[CAR_CLASS_SNAPSHOT_KEY]

  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
    return null
  }

  const position = Number(snapshot.position)

  if (
    typeof snapshot.id !== 'string' ||
    typeof snapshot.name !== 'string' ||
    !Number.isInteger(position) ||
    position < 1
  ) {
    return null
  }

  return {
    id: snapshot.id,
    name: snapshot.name,
    position,
  }
}

export function isClassScoredSession(session) {
  return (
    Number(asMetadata(session?.metadata)[CAR_CLASS_SCORING_VERSION_KEY]) ===
    CAR_CLASS_SCORING_VERSION
  )
}

export function getScoringPosition(result) {
  if (!isClassScoredSession(result?.session)) {
    return Number(result?.racePosition)
  }

  return getChampionshipCarClassSnapshot(result)?.position ?? null
}

export function getResultCurrentCarClass(result, championshipId) {
  if (!result?.user?.id) return null

  const attendee = result.user.attendingChampionships?.find(
    (entry) => entry.championshipId === championshipId,
  )

  if (!attendee?.carClassId) return null

  return attendee.carClass
    ? {
        id: attendee.carClass.id,
        name: attendee.carClass.name,
      }
    : {
        id: attendee.carClassId,
        name: '',
      }
}

export function getResultEditorCarClass(
  result,
  championshipId,
  configuredCarClasses = [],
) {
  const pendingCarClassId = result?.metadata?.pendingCarClassId

  if (pendingCarClassId) {
    const pendingCarClass = configuredCarClasses.find(
      (carClass) => carClass.id === pendingCarClassId,
    )

    if (pendingCarClass) return pendingCarClass
  }

  const resultCarClassId = asMetadata(result?.metadata)[RESULT_CAR_CLASS_KEY]

  if (typeof resultCarClassId === 'string') {
    const resultCarClass = configuredCarClasses.find(
      (carClass) => carClass.id === resultCarClassId,
    )

    if (resultCarClass) return resultCarClass
  }

  const snapshot = getChampionshipCarClassSnapshot(result)

  if (snapshot) {
    const snapshotCarClass = configuredCarClasses.find(
      (carClass) => carClass.id === snapshot.id,
    )

    if (snapshotCarClass) return snapshotCarClass
  }

  const attendeeCarClass = getResultCurrentCarClass(result, championshipId)

  if (
    attendeeCarClass &&
    (configuredCarClasses.length === 0 ||
      configuredCarClasses.some(
        (carClass) => carClass.id === attendeeCarClass.id,
      ))
  ) {
    return attendeeCarClass
  }

  return configuredCarClasses.length === 1 ? configuredCarClasses[0] : null
}

export function withResultChampionshipCarClass(metadata, carClassId) {
  const nextMetadata = { ...asMetadata(metadata) }

  if (carClassId) {
    nextMetadata[RESULT_CAR_CLASS_KEY] = carClassId
  } else {
    delete nextMetadata[RESULT_CAR_CLASS_KEY]
  }

  return nextMetadata
}

export function updateResultCurrentCarClass(result, championshipId, carClass) {
  if (result?.user?.id == null) return result

  const attendingChampionships = Array.isArray(
    result.user.attendingChampionships,
  )
    ? result.user.attendingChampionships
    : []
  const existingIndex = attendingChampionships.findIndex(
    (entry) => entry.championshipId === championshipId,
  )
  const nextEntry = {
    ...(existingIndex >= 0 ? attendingChampionships[existingIndex] : {}),
    championshipId,
    carClassId: carClass?.id ?? null,
    carClass: carClass ?? null,
  }
  const nextAttendingChampionships = [...attendingChampionships]

  if (existingIndex >= 0) {
    nextAttendingChampionships[existingIndex] = nextEntry
  } else {
    nextAttendingChampionships.push(nextEntry)
  }

  return {
    ...result,
    user: {
      ...result.user,
      attendingChampionships: nextAttendingChampionships,
    },
  }
}

export function stageResultDriverCarClassAssignment(
  results,
  championshipId,
  userId,
  carClass,
) {
  return results.map((result) =>
    result.user?.id === userId
      ? {
          ...updateResultCurrentCarClass(result, championshipId, carClass),
          metadata: {
            ...withResultChampionshipCarClass(result.metadata, carClass.id),
            pendingCarClassId: carClass.id,
          },
        }
      : result,
  )
}

export function buildChampionshipCarClassSnapshots({
  results,
  attendees,
  configuredCarClasses,
}) {
  const classesById = new Map(
    configuredCarClasses.map((carClass) => [carClass.id, carClass]),
  )
  const attendeesByUserId = new Map(
    attendees.map((attendee) => [attendee.userId, attendee]),
  )
  const missingDrivers = []
  const assignedResults = []
  const soleConfiguredCarClass =
    configuredCarClasses.length === 1 ? configuredCarClasses[0] : null

  for (const result of results) {
    const attendee = result.userId ? attendeesByUserId.get(result.userId) : null
    const resultCarClassId = asMetadata(result.metadata)[RESULT_CAR_CLASS_KEY]
    const resultCarClass = classesById.get(resultCarClassId)
    const snapshot = getChampionshipCarClassSnapshot(result)
    const snapshotCarClass = snapshot ? classesById.get(snapshot.id) : null
    const attendeeCarClass = result.userId
      ? classesById.get(attendee?.carClassId)
      : null
    // A result-level assignment is an explicit editor override. Otherwise,
    // retain the published snapshot before falling back to the driver's current
    // championship class so republishing cannot rewrite historical rounds.
    const explicitlyAssignedCarClass =
      resultCarClass ?? snapshotCarClass ?? attendeeCarClass
    const carClass = explicitlyAssignedCarClass ?? soleConfiguredCarClass

    if (!carClass) {
      missingDrivers.push({
        resultId: result.id,
        userId: result.userId ?? null,
        displayName:
          result.user?.username ||
          [result.user?.firstName, result.user?.lastName]
            .filter(Boolean)
            .join(' ') ||
          result.unclaimedDriver?.name ||
          result.unclaimedDriverMeta?.name ||
          'Unknown driver',
      })
      continue
    }

    assignedResults.push({ result, carClass })
  }

  const uniqueMissingDrivers = Array.from(
    new Map(
      missingDrivers.map((driver) => [
        driver.userId ?? driver.resultId,
        driver,
      ]),
    ).values(),
  ).map(({ resultId: _resultId, ...driver }) => driver)

  if (uniqueMissingDrivers.length > 0) {
    return { missingDrivers: uniqueMissingDrivers, snapshots: [] }
  }

  const groups = new Map()

  for (const assignedResult of assignedResults) {
    const splitId = assignedResult.result.metadata?.splitId || 'no-split'
    const key = `${splitId}:${assignedResult.carClass.id}`
    const group = groups.get(key) ?? []
    group.push(assignedResult)
    groups.set(key, group)
  }

  const snapshots = []

  for (const group of groups.values()) {
    group
      .sort((a, b) => {
        const positionDifference =
          Number(a.result.racePosition ?? Number.MAX_SAFE_INTEGER) -
          Number(b.result.racePosition ?? Number.MAX_SAFE_INTEGER)

        return positionDifference || a.result.id.localeCompare(b.result.id)
      })
      .forEach(({ result, carClass }, index) => {
        snapshots.push({
          resultId: result.id,
          snapshot: {
            id: carClass.id,
            name: carClass.name,
            position: index + 1,
          },
        })
      })
  }

  return { missingDrivers: [], snapshots }
}

export function withChampionshipCarClassSnapshot(metadata, snapshot) {
  const nextMetadata = { ...asMetadata(metadata) }

  if (snapshot) {
    nextMetadata[CAR_CLASS_SNAPSHOT_KEY] = snapshot
  } else {
    delete nextMetadata[CAR_CLASS_SNAPSHOT_KEY]
  }

  return nextMetadata
}

export function withClassScoringVersion(metadata, enabled) {
  const nextMetadata = { ...asMetadata(metadata) }

  if (enabled) {
    nextMetadata[CAR_CLASS_SCORING_VERSION_KEY] = CAR_CLASS_SCORING_VERSION
  } else {
    delete nextMetadata[CAR_CLASS_SCORING_VERSION_KEY]
  }

  return nextMetadata
}

function getEffectiveTime(result) {
  const time = Number(result?.time)
  const penaltyTime = Number(result?.penaltyTime ?? 0)

  if (!Number.isFinite(time)) return null

  return time + (Number.isFinite(penaltyTime) ? penaltyTime : 0)
}

export function getClassRelativeTiming(result, classLeader, overallLeader) {
  if (!result || !classLeader || result.status !== 'finished') return null

  const classLeaderUserId = classLeader.user?.id

  if (
    result === classLeader ||
    (classLeaderUserId && result.user?.id === classLeaderUserId)
  ) {
    const absoluteTime = Number(result.absoluteTime)

    if (Number.isFinite(absoluteTime) && absoluteTime >= 0) {
      return { time: absoluteTime, type: 'TIME', penaltyTime: 0 }
    }

    const overallTime = Number(overallLeader?.time)
    const overallGap = Number(result.time)

    if (
      result.type === 'GAP' &&
      overallLeader?.type === 'TIME' &&
      Number.isFinite(overallTime) &&
      Number.isFinite(overallGap)
    ) {
      return {
        time: overallTime + overallGap,
        type: 'TIME',
        penaltyTime: 0,
      }
    }

    return result.type === 'TIME' ? null : { displayValue: 'Leader' }
  }

  if (
    Number(result.classRelativeTimingVersion) ===
    CLASS_RELATIVE_TIMING_VERSION
  ) {
    return ['GAP', 'LAP'].includes(result.type)
      ? { time: Number(result.time), type: result.type, penaltyTime: 0 }
      : null
  }

  if (result.type === 'LAP' && classLeader.type === 'LAP') {
    const lapDifference = Number(result.time) - Number(classLeader.time)

    return Number.isFinite(lapDifference) && lapDifference > 0
      ? { time: lapDifference, type: 'LAP', penaltyTime: 0 }
      : { displayValue: 'Leader' }
  }

  const resultTime = getEffectiveTime(result)
  const leaderTime = getEffectiveTime(classLeader)

  if (resultTime == null || leaderTime == null) return null

  if (result.type === 'GAP') {
    const leaderOffset =
      classLeader.type === 'GAP'
        ? leaderTime
        : Number(classLeader.penaltyTime ?? 0)
    return {
      time: Math.max(0, resultTime - leaderOffset),
      type: 'GAP',
      penaltyTime: 0,
    }
  }

  if (result.type === 'TIME' && classLeader.type === 'TIME') {
    return {
      time: Math.max(0, resultTime - leaderTime),
      type: 'GAP',
      penaltyTime: 0,
    }
  }

  return null
}

export function getOverallRelativeTiming(
  result,
  overallLeader,
  { sessionType } = {},
) {
  if (!result || !overallLeader || result.status !== 'finished') return null

  const overallLeaderUserId = overallLeader.user?.id

  if (
    result === overallLeader ||
    (overallLeaderUserId && result.user?.id === overallLeaderUserId)
  ) {
    return { displayValue: 'Leader' }
  }

  const shouldCompareLaps =
    sessionType == null || String(sessionType).trim().toLowerCase() === 'race'

  if (shouldCompareLaps) {
    const resultLaps = Number(result.laps)
    const leaderLaps = Number(overallLeader.laps)
    const lapDifference = leaderLaps - resultLaps

    if (
      Number.isFinite(resultLaps) &&
      Number.isFinite(leaderLaps) &&
      lapDifference > 0
    ) {
      return { time: lapDifference, type: 'LAP', penaltyTime: 0 }
    }
  }

  return getClassRelativeTiming(result, overallLeader)
}
